import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Menu,
  MenuItem,
  Typography,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CATEGORIES_FILTER_API,
  CONTEST_ENTRIES_API,
  CONTEST_RESULT_ANNOUNCED_API,
  POST_BY_IDS_API,
  USER_BY_IDS_API
} from '../../api/endpoint';
import { ConstructionOutlined, Height, Padding, Timeline } from '@mui/icons-material';
import { ContestDetail, UserDetail } from '../../types';
import React, { useEffect, useRef, useState } from 'react';
import backBtn from '../../assets/btnBack.png';
import axiosInstance from '../../api/axiosConfig';
import createAxiosInstance from '../../api/axiosConfig';
import style from '../contests/style.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import Loader from '../../components/loader';
import scoreIcon from '../../assets/QuestCoinsIcon.png';
import categoryIcon from '../../assets/categoryIcon.png';
import ProfileImageBlur from '../../components/profileImageBlur';
import submitBtn from '../../assets/workSubmit.png';
import DownloadApp from '../../components/downloadPrompt';
import shareBtn from '../../assets/shareBtn.png';
import copylinkIcon from '../../assets/copy-link.png';
import whatsapplogo from '../../assets/whatsapplogo.jpg';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    backdropFilter: 'blur(7px)'
  },
  '& .MuiDialog-paper': {
    borderRadius: theme.breakpoints.up('sm') ? '20px' : '18px',
    backdropFilter: 'blur(7px)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
    width: useMediaQuery(theme.breakpoints.down('sm')) ? '85%' : '60%',
    maxWidth: 'none',
    height: useMediaQuery(theme.breakpoints.down('sm')) ? '87%' : '80%',
    backgroundColor: 'transparent'
  },
  '& .MuiDialogContent-root': {
    padding: 0,
    color: 'none'
  }
}));

interface ContestEntry {
  entry_id: string;
  work_id: string;
  user_id: string;
  entry_code: string;
  work_ids: string[];
  contest_id: string;
  score: number;
  position: number;
  title: string;
  type: string;
}

interface ContestDetailDialogProps {
  open: boolean;
  setDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  contestDetail?: ContestDetail | null;
  timeline?: string;
}

const ContestDetailDialog: React.FC<ContestDetailDialogProps> = ({ open, setDialogOpen, contestDetail, timeline }) => {
  const [userData, setUserData] = useState<UserDetail[]>([]);
  const [entries, setEntries] = useState<ContestEntry[] | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [works, setWorks] = useState<any>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pageKey, SetPageKey] = useState<string | null>('');
  const [title, setTitle] = useState('All');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [nextPage, setNextPage] = useState(1);
  const navigate = useNavigate();
  const [postData, setPostData] = useState<any>([]);
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const maxChars = 300;
  const fullText = contestDetail?.description || '';
  const isTruncated = !expanded && fullText.length > maxChars;
  const displayText = isTruncated ? fullText.slice(0, maxChars) + '...' : fullText;
  const [openDownloadPrompt, setOpenDownloadPrompt] = useState(false);
  const isQuest = location.pathname === `/quest`;
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);

  const transformedData = async (postDetail: any, userDetail: any, entries: any) => {
    const transformed = postDetail.data.data.map((item: any) => {
      const user = userDetail.data.data.find((user: { uid: any }) => user.uid === item.user_id);
      const entry = entries.data.data.find((entry: { work_id: any; work_ids: any[] }) => {
        const workId = entry.work_id ? entry.work_id : entry.work_ids[0];
        return workId === item.post_id;
      });

      return {
        post_id: item.post_id,
        user_id: item.user_id,
        post_file_name: item.files[0].name,
        user_file_name: user.avatar,
        defaultAvatar: user.defaultAvatar,
        entryStatus: entry.entry_status
      };
    });
    return transformed;
  };

  const fetchContestEntries = async () => {
    if (contestDetail) {
      setLoading(true);
      try {
        if (isQuest) {
          const axiosInstance = createAxiosInstance(false);
          const response = await axiosInstance.get(
            `${CONTEST_ENTRIES_API}/${contestDetail.contest_id}/entries?contest_type=MICRO_CONTEST`
          );
          const ids = response.data.data.map((item: any) => (item.work_id ? item.work_id : item.work_ids[0])).join(',');
          const userIds = response.data.data.map((item: any) => item.user_id).join(',');
          const postDetail = await axiosInstance.get(`${POST_BY_IDS_API}${ids}`);
          const userDetail = await axiosInstance.get(`${USER_BY_IDS_API}${userIds}`);

          const data = await transformedData(postDetail, userDetail, response);

          setPostData(data);
        } else {
          const axiosInstance = createAxiosInstance(true);
          if (timeline === 'completed') {
            const response = await axiosInstance.get(
              `${CONTEST_RESULT_ANNOUNCED_API}/${contestDetail.contest_id}/All.json`
            );

            if (response.data.data) {
              if (title === 'All') {
                setPostData(response.data.data);
              } else if (title === 'Winners') {
                const filteredEntries = response.data.data?.filter((entry: any) => entry.type === 'Winner');
                setPostData(filteredEntries);
              } else {
                const filteredEntries = response.data.data?.filter(
                  (entry: any) => entry.title === title.replace(/\s+/g, '')
                );
                setPostData(filteredEntries);
              }
              if (response.data.page) {
                SetPageKey(response.data.page);
              } else {
                SetPageKey(null);
              }
            }
          } else {
            const axiosInstance = createAxiosInstance(false);
            const response = await axiosInstance.get(
              `${CONTEST_ENTRIES_API}/${contestDetail.contest_id}/entries?contest_type=CONTEST`
            );
            const userIds = response.data.data.map((item: any) => item.user_id).join(',');
            const userDetail = await axiosInstance.get(`${USER_BY_IDS_API}${userIds}`);
            setEntries(userDetail.data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching contest entries:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleShareToWhatsApp = () => {
    const currentURL = window.location.origin + location.pathname + location.search;
    const whatsappURL = `https://wa.me/?text=${encodeURIComponent(currentURL)}`;
    window.open(whatsappURL, '_blank');
  };

  const handleCopy = () => {
    const currentPath = window.location.origin + location.pathname + location.search;
    navigator.clipboard.writeText(currentPath).catch((err) => {
      console.error('Failed to copy: ', err);
    });
  };

  const handleCategoryFilter = async (item: any) => {
    setTitle(item.display);
  };

  // const handleScroll = () => {
  //   if (scrollContainerRef.current) {
  //     const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
  //     if (scrollTop + clientHeight >= scrollHeight - 10 && !isLoading && pageKey !== null) {
  //       setNextPage((prevPage) => prevPage + 1);
  //     }
  //   }
  // };

  // useEffect(() => {
  //   const container = scrollContainerRef.current;
  //   if (container) {
  //     container.addEventListener('scroll', handleScroll);
  //   }
  //   return () => {
  //     if (container) {
  //       container.removeEventListener('scroll', handleScroll);
  //     }
  //   };
  // }, []);

  const handleBackClick = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleSubmit = () => {
    setOpenDownloadPrompt(true);
  };

  useEffect(() => {
    if (contestDetail) {
      fetchContestEntries();
    }
  }, [nextPage, title]);

  if (!contestDetail) return null;

  const imageUrl = contestDetail.cr_banner
    ? `${process.env.REACT_APP_DEFAULT_AVATAR}/${isQuest ? 'MICRO_CONTESTS' : 'CONTESTS'}/${contestDetail?.contest_id}/IMAGES/medium/${contestDetail?.ct_banner}`
    : '';
  const handlePostClick = (postId: string) => {
    navigate(`/work/${postId}`);
  };

  const handleProfileClick = (handle: any) => {
    navigate(`/user/${handle}`);
  };

  const handleShare = (event: any) => {
    console.log('handleClick');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const CompletedContest: React.FC<{ categories: any[] }> = ({ categories }) => (
    <Box>
      {!isQuest && (
        <Box sx={{ margin: isMobile ? '15px 0 5px 20px' : '32px 0 10px 30px' }}>
          {categories.map((item) => (
            <Box className={style.categories} key={item.id}>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  padding: isMobile ? '3px' : '7px',
                  textAlign: 'center',
                  fontFamily: 'Baloo2',
                  cursor: 'pointer',
                  fontSize: isMobile ? '10px' : '14.7px'
                }}
                className={style['white-grad']}
                onClick={() => handleCategoryFilter(item)}>
                {`${item.display}${item.display === 'All' ? ` (${contestDetail.enrolled})` : ` (${item.count})`}`}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {isLoading && !postData && <Loader />}
      <Box
        sx={{
          minHeight: isMobile ? '100vh' : 'auto',
          width: '100%'
        }}>
        {postData ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              padding: isMobile ? '3% 7% 7%' : '3% 4% 4%',
              gridGap: '12px',
              justifyContent: 'center'
            }}>
            {postData?.map((item: any, index: number) => {
              return (
                <Box
                  key={index}
                  className={style.detail_cardView}
                  sx={{
                    height: isMobile ? '145px' : '170px',
                    width: isMobile ? '145px' : '170px',
                    position: 'relative',
                    zIndex: 0
                  }}>
                  <Box onClick={() => handlePostClick(item.post_id)}>
                    <img
                      src={`${process.env.REACT_APP_DEFAULT_AVATAR}/${item.user_id}/WORKS/IMAGES/medium/${item.post_file_name}`}
                      alt="Work"
                      style={{
                        height: '100%',
                        width: '100%',
                        filter: isQuest && item.entryStatus !== 3 ? 'blur(20px)' : 'none'
                      }}
                    />

                    {(item.entryStatus === 1 || item.entryStatus === 4) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '40%',
                          backgroundColor: 'rgb(255,255,255,0.8)',
                          color: 'black',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: isMobile ? '10px' : '12px',
                          fontWeight: 'bold',
                          zIndex: 3,
                          alignItems: 'center',
                          width: '100%',
                          textAlign: 'center'
                        }}>
                        {item.entryStatus === 1 ? 'In Review' : 'Rejected'}
                      </Box>
                    )}
                  </Box>

                  <Box className={style.avatarContainer}>
                    {/* <Avatar
                      src={
                        item?.defaultAvatar
                          ? `${process.env.REACT_APP_DEFAULT_AVATAR}/APP/UserAvatars/${isQuest ? item.user_file_name : item?.avatar}`
                          : `${process.env.REACT_APP_DEFAULT_AVATAR}/${item?.user_id}/PROFILE/IMAGES/medium/${item?.user_file_name}`
                      }
                      style={isMobile ? { width: '23px', height: '23px' } : { width: '35px', height: '35px' }}
                    /> */}
                    <ProfileImageBlur
                      imgHeight={isMobile ? '23px' : '35px'}
                      imgWidth={isMobile ? '23px' : '35px'}
                      uid={item?.user_id}
                      avatar={item.user_file_name}
                      defaultAvatar={item?.defaultAvatar ? item?.defaultAvatar : item.isDefault}
                    />
                    <Typography
                      sx={{
                        color: 'white',
                        textAlign: 'center',
                        padding: isMobile ? '0 0 0 5px' : '0 0 0 2px',
                        fontSize: isMobile ? '12px' : '14.7px'
                      }}>
                      {item?.name?.length && item?.name?.length > 11
                        ? `${item?.name?.substring(0, 11)}...`
                        : item?.name}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box>
            <Typography
              sx={{
                mt: 2,
                color: contestDetail.theme,
                padding: isMobile ? '0 0 0 15px' : '0 0 0 30px',
                fontSize: isMobile ? '17px' : '30px',
                fontFamily: 'somatic'
              }}>
              No Entries Data
            </Typography>{' '}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {openDownloadPrompt && <DownloadApp openDialog={openDownloadPrompt} setOpenDialog={setOpenDownloadPrompt} />}
      <BootstrapDialog open={open} maxWidth="md" fullWidth scroll="body">
        <DialogContent
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            height: '100%',
            scrollbarWidth: 'none',
            padding: '16px'
          }}>
          <Box className={style.detailPage}>
            <Box sx={{ height: isMobile ? '40%' : '80%' }}>
              <img src={imageUrl} className={style.detailImg} alt={'work'}></img>
            </Box>
            <Button
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openMenu ? 'true' : undefined}
              style={{ position: 'absolute', top: isMobile ? '2%' : '5%', right: '0' }}
              onClick={handleShare}>
              <img alt={'share'} src={shareBtn}></img>
            </Button>

            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleClose}
              container={document.body}
              MenuListProps={{
                'aria-labelledby': 'basic-button'
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              sx={{
                '& .MuiPaper-root': {
                  zIndex: 2000,
                  width: isMobile ? '100px' : '150px',
                  height: isMobile ? '75px' : '100px',
                  background: 'rgba(255, 255, 255, 0.8)',
                  color: 'black',
                  backdropFilter: 'blur(12px)'
                },
                '& .MuiMenuItem-root': {
                  minHeight: isMobile ? 15 : 26,
                  paddingTop: isMobile ? '2px' : '4px',
                  paddingBottom: isMobile ? '2px' : '4px'
                }
              }}>
              <MenuItem onClick={handleCopy}>
                <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
                  <img
                    alt="copy-link"
                    src={copylinkIcon}
                    style={{ width: isMobile ? 18 : 24, height: isMobile ? 18 : 24 }}
                  />
                  <Typography style={{ fontSize: isMobile ? '12px' : '18px' }}>Copy URL</Typography>
                </Box>
              </MenuItem>

              <Divider />

              <MenuItem onClick={handleShareToWhatsApp}>
                <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
                  <img
                    alt="whatsapp-logo"
                    src={whatsapplogo}
                    style={{ width: isMobile ? 18 : 24, height: isMobile ? 18 : 24 }}
                  />
                  <Typography style={{ fontSize: isMobile ? '12px' : '18px' }}>Share</Typography>
                </Box>
              </MenuItem>
            </Menu>

            {isQuest && (
              <Button
                style={{
                  position: 'absolute',
                  bottom: isMobile ? '60%' : '20%',
                  left: isMobile ? '20%' : '27%',
                  zIndex: 2,
                  cursor: 'pointer',
                  color: 'black',
                  padding: 0,
                  border: 'none',
                  background: 'none'
                }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Button onClick={handleSubmit}>
                    <img
                      src={submitBtn}
                      alt="submit"
                      style={{ display: 'block', height: 'auto', width: isMobile ? '200px' : '300px' }}
                    />
                  </Button>
                  <span
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '10px' : '16px',
                      pointerEvents: 'none',
                      fontFamily: 'Baloo2'
                    }}>
                    Submit your Work
                  </span>
                </div>
              </Button>
            )}

            <Button
              onClick={handleBackClick}
              style={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 2,
                cursor: 'pointer',
                boxShadow: 'rgba(0, 0, 0, 0.2) 0px 8px 24px',
                color: 'black'
              }}>
              <img
                src={backBtn}
                alt="backBtn"
                style={{
                  width: isMobile ? 24 : 32,
                  height: isMobile ? 24 : 32
                }}
              />
            </Button>
            <Box className={style.detailName}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                <Typography
                  sx={{
                    background:
                      'linear-gradient(89.92deg, #E02780 0.07%, #FF5D83 0.08%, #B465FF 38.02%, #FF9D32 62.98%, #FF7A52 83.45%, #FF4FF1 99.93%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '17px' : '30px',
                    fontFamily: 'somatic',
                    marginRight: '10px',
                    textAlign: 'center'
                  }}>
                  {contestDetail.contest_code}
                </Typography>

                <Typography
                  sx={{
                    color: contestDetail.theme,
                    fontSize: isMobile ? '17px' : '30px',
                    fontFamily: 'somatic'
                  }}>
                  {contestDetail.title}
                </Typography>
              </div>

              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1,
                    color: '#666',
                    padding: isMobile ? '0 0 0 15px' : '0 14px 0 30px',
                    fontFamily: 'somatic',
                    fontSize: isMobile ? '12px' : '14.7px',
                    whiteSpace: 'pre-line'
                  }}
                  className={style.detail_desc}>
                  {displayText}
                </Typography>

                {fullText.length > maxChars && (
                  <Box
                    sx={{
                      padding: isMobile ? '0 0 0 15px' : '0 14px 0 30px',
                      mt: 0.5,
                      cursor: 'pointer',
                      color: '#1976d2',
                      fontSize: isMobile ? '12px' : '14px'
                    }}
                    onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'See less' : 'See more'}
                  </Box>
                )}
              </Box>

              {isQuest && (
                <>
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? '10%' : '20%',
                      margin: '2% 0'
                    }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px' }}>
                      <img
                        alt="score-icon"
                        style={{
                          height: isMobile ? '20px' : 'auto',
                          width: isMobile ? '20px' : 'auto'
                        }}
                        src={scoreIcon}
                      />
                      <Typography
                        style={{ whiteSpace: 'nowrap' }}>{`${contestDetail.winning_points} Points`}</Typography>
                    </Box>

                    <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <img
                        alt="category-icon"
                        style={{
                          height: isMobile ? '20px' : 'auto',
                          width: isMobile ? '20px' : 'auto'
                        }}
                        src={categoryIcon}
                      />
                      <Typography style={{ whiteSpace: 'nowrap' }}>{contestDetail.category}</Typography>
                    </Box>
                  </Box>

                  {contestDetail.sponsors && (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                      <Typography style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Sponsored by:</Typography>
                      <img
                        alt="sponsorLogo"
                        src={`${process.env.REACT_APP_DEFAULT_AVATAR}/APP/SponsorAvatars/${contestDetail.sponsors[0].avatar}`}
                        style={{
                          width: isMobile ? '40px' : '80px',
                          height: isMobile ? '40px' : '80px',
                          objectFit: 'cover',
                          borderRadius: isMobile ? '9px' : '30px'
                        }}
                      />
                    </Box>
                  )}

                  {contestDetail.tags && (
                    <Box
                      style={{
                        margin: ' 2% 10%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                      }}>
                      {contestDetail.tags.map((tag: any, index: number) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(180px)',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '20px',
                            padding: '5px 10px',
                            width: 'fit-content'
                          }}>
                          <Typography
                            sx={{
                              fontFamily: 'Baloo2',
                              color: '#666',
                              fontSize: isMobile ? '12px' : '21px',
                              fontWeight: 700,
                              textAlign: 'center',
                              whiteSpace: 'nowrap'
                            }}>
                            {tag.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}

              {(timeline === 'completed' || isQuest) && (
                <CompletedContest categories={contestDetail.contest_entry_categories} />
              )}
              {timeline === 'ongoing' && (
                <>
                  {' '}
                  <Box sx={{ display: 'flex', mt: 1 }}>
                    <Typography
                      sx={{
                        mt: 1,
                        color: '#666',
                        padding: isMobile ? '0 0 0 15px' : '0 0 0 30px',
                        fontSize: isMobile ? '12px' : '10px',
                        fontFamily: 'somatic'
                      }}>{`Strats At:`}</Typography>
                    <Typography
                      sx={{
                        mt: 1,
                        color: '#333',
                        fontSize: isMobile ? '12px' : '10px',
                        fontFamily: 'somatic',
                        fontWeight: '800',
                        padding: '0 0 0 5px'
                      }}>
                      {contestDetail.from.split('T')[0]}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 1,
                        color: '#666',
                        padding: '0 0 0 30px',
                        fontSize: isMobile ? '12px' : '10px',
                        fontFamily: 'somatic'
                      }}>{`Ends At:`}</Typography>

                    <Typography
                      sx={{
                        mt: 1,
                        color: '#333',
                        fontSize: isMobile ? '12px' : '10px',
                        fontFamily: 'somatic',
                        fontWeight: '800',
                        padding: '0 0 0 5px'
                      }}>{` ${contestDetail.to.split('T')[0]}`}</Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: isMobile ? '1rem' : '1.5rem',
                      padding: isMobile ? '12px 0 0 15px' : '32px 0 0 30px'
                    }}>{`Enrolled (${contestDetail.enrolled})`}</Typography>
                  <Box className={style.detail_userEnroll}>
                    {entries &&
                      entries.map((item: any, index: number) => (
                        <Box key={`${item.uid}-${index}`}>
                          <Avatar
                            src={`${process.env.REACT_APP_DEFAULT_AVATAR}/${item?.uid}/PROFILE/IMAGES/medium/${item?.avatar}`}
                            sx={{ width: 80, height: 80 }}
                          />
                          <Typography
                            sx={{
                              textAlign: 'center',
                              padding: '4px',
                              fontSize: isMobile ? '13px' : '14.7px'
                            }}>
                            {item?.firstname?.length > 7 ? `${item.firstname.substring(0, 7)}...` : item.firstname}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
      </BootstrapDialog>
    </>
  );
};

export default ContestDetailDialog;
